import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SITE } from '../mock';
import { accountsApi } from '../lib/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronRight, LayoutGrid, ShieldCheck, Star, Send, Facebook, MessageCircle, Copy, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const ProductDetail = () => {
  const { slug } = useParams();
  const [account, setAccount] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    Promise.all([accountsApi.get(slug), accountsApi.list({ limit: 8 })])
      .then(([r1, r2]) => {
        if (cancel) return;
        setAccount(r1.data);
        setActiveImg(r1.data.image);
        setRelated(r2.data.filter((a) => a.id !== r1.data.id).slice(0, 4));
      })
      .catch(() => setAccount(null))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [slug]);

  const gallery = useMemo(() => {
    if (!account) return [];
    const g = account.gallery && account.gallery.length ? account.gallery : [account.image];
    return g;
  }, [account]);

  const openTelegram = () => {
    if (!account) return;
    const message = `Hi! I'm interested in buying: ${account.title} ($${account.price.toFixed(2)}) - ID: ${account.id}`;
    const url = `${SITE.telegram}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    toast.success('Opening Telegram...', { description: 'Contact the seller directly to complete your purchase.' });
  };

  const openFacebook = () => {
    window.open(SITE.facebook, '_blank');
    toast.success('Opening Facebook Page...', { description: 'Message us for support and inquiries.' });
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return (
      <div className="bg-marketplace min-h-screen">
        <Header />
        <div className="grid place-items-center py-40"><Loader2 className="h-10 w-10 animate-spin text-amber-300" /></div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="bg-marketplace min-h-screen">
        <Header />
        <div className="max-w-2xl mx-auto text-center py-32 px-4">
          <h1 className="text-2xl font-bold text-white">Listing not found</h1>
          <p className="text-slate-400 mt-2">This account may have been sold or removed.</p>
          <Link to="/market/free-fire/accounts" className="inline-block mt-6 h-11 px-6 bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-md font-semibold leading-[44px]">
            Browse other accounts
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-marketplace min-h-screen">
      <Header />
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 pt-6">
        <div className="flex items-center gap-2 text-sm text-slate-300 mb-5">
          <LayoutGrid className="h-4 w-4" />
          <Link to="/" className="hover:text-amber-300">All markets</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to="/market/free-fire/accounts" className="hover:text-amber-300">Free Fire</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to="/market/free-fire/accounts" className="hover:text-amber-300">Accounts</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-amber-300 truncate max-w-[300px]">{account.title.slice(0, 40)}...</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="rounded-xl overflow-hidden bg-[#0b1530] border border-white/10">
              <img src={activeImg} alt={account.title} className="w-full max-h-[520px] object-contain" />
            </div>
            {gallery.length > 1 && (
              <div className="mt-3">
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 max-h-[360px] overflow-y-auto scrollbar-slim pr-1">
                  {gallery.map((g, i) => (
                    <button key={`${g}-${i}`} onClick={() => setActiveImg(g)}
                      className={`aspect-square rounded-lg overflow-hidden border transition-colors ${activeImg === g ? 'border-amber-300' : 'border-white/10 hover:border-white/30'}`}>
                      <img src={g} alt={`preview-${i}`} loading="lazy" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">{gallery.length} photo{gallery.length > 1 ? 's' : ''} · click to preview</p>
              </div>
            )}

            <div className="mt-8 bg-white/5 border border-white/10 rounded-xl p-5">
              <h3 className="font-semibold text-white text-lg mb-3">Description</h3>
              <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{account.description || 'No description provided.'}</p>
              {account.details && Object.keys(account.details).length > 0 && (
                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(account.details).map(([k, v]) => v ? (
                    <div key={k} className="bg-[#0b1530] rounded-lg p-3 border border-white/5">
                      <div className="text-[11px] uppercase tracking-wide text-slate-400">{k}</div>
                      <div className="text-white font-semibold mt-1">{v}</div>
                    </div>
                  ) : null)}
                </div>
              )}
            </div>

            <div className="mt-6 bg-white/5 border border-white/10 rounded-xl p-5">
              <h3 className="font-semibold text-white text-lg mb-3">Delivery & Warranty</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400 mt-0.5" /> Instant handover via Telegram after payment confirmation.</li>
                <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400 mt-0.5" /> Full binding transfer (Facebook / Guest / VK).</li>
                <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400 mt-0.5" /> 7-day recovery warranty for eligible accounts.</li>
                <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400 mt-0.5" /> All screenshots verified before listing.</li>
              </ul>
            </div>
          </div>

          <div className="space-y-5">
            <div className="bg-[#111b38] border border-white/10 rounded-xl p-5 sticky top-20">
              <h1 className="text-lg font-bold text-white leading-snug">{account.title}</h1>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="price-glow text-3xl font-black">${account.price.toFixed(2)}</span>
                <span className="text-xs text-slate-400">USD</span>
              </div>

              <div className="mt-4 flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-white/5">
                {account.seller?.avatar ? (
                  <img src={account.seller.avatar} alt={account.seller?.name} className="h-11 w-11 rounded-full object-cover" />
                ) : (
                  <div className="h-11 w-11 rounded-full bg-white/10" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-semibold truncate">{account.seller?.name || 'Seller'}</span>
                    {account.seller?.online && <span className="h-2 w-2 rounded-full bg-emerald-400" />}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-amber-300">
                    <Star className="h-3 w-3 fill-amber-300" />
                    {account.seller?.rating ?? 5} • <span className="text-slate-400">({account.seller?.reviews ?? 0} reviews)</span>
                  </div>
                </div>
              </div>

              <div className="mt-5 space-y-2.5">
                <button onClick={openTelegram} className="tg-btn w-full h-12 rounded-lg font-bold flex items-center justify-center gap-2 shadow-lg shadow-sky-500/20">
                  <Send className="h-5 w-5" /> Buy with Telegram
                </button>
                <button onClick={openFacebook} className="fb-btn w-full h-12 rounded-lg font-bold flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20">
                  <Facebook className="h-5 w-5" /> Facebook Page
                </button>
                <Button variant="outline" onClick={copyLink} className="w-full h-11 bg-transparent border-white/10 text-slate-200 hover:bg-white/5 hover:text-white">
                  {copied ? <><Check className="h-4 w-4 mr-2 text-emerald-400" />Link copied</> : <><Copy className="h-4 w-4 mr-2" />Share this listing</>}
                </Button>
              </div>

              <div className="mt-5 pt-5 border-t border-white/5 text-xs text-slate-400 space-y-2">
                <div className="flex items-center gap-2"><MessageCircle className="h-4 w-4 text-sky-400" /> Chat with seller before purchase</div>
                <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Buyer protection until delivery</div>
              </div>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="text-xl lg:text-2xl font-bold text-white mb-5">You may also like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map((a) => (
                <Link key={a.id} to={`/market/free-fire/accounts/p/${a.slug}`} className="card-hover bg-[#111b38]/80 border border-white/5 rounded-xl overflow-hidden">
                  <div className="aspect-[4/5] bg-[#0b1530]">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <div className="text-[12px] text-white line-clamp-2 leading-snug">{a.title}</div>
                    <div className="mt-2 price-glow font-bold">${a.price.toFixed(2)}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ProductDetail;
